<template>
  <div id="contact-form">
    <div id="form-content" class="form-content">
      <form id="contactForm" class="contact-form" @submit.prevent="sendEmail" ref="contactForm">
        <div class="spam-trap" aria-hidden="true">
          <label for="contact-website">Leave this field empty</label>
          <input
            id="contact-website"
            type="text"
            name="website"
            tabindex="-1"
            autocomplete="off"
          />
        </div>
        <input type="hidden" name="form_started_at" :value="formStartedAt" />
        <div class="form-group pt-2">
          <label>Name *</label>
          <input class="input-name" type="text" name="user_name" maxlength="100" required />
        </div>
        <div class="form-group pt-2">
          <label>Email *</label>
          <input type="email" name="user_email" maxlength="254" required />
        </div>
        <div class="form-group pt-2">
          <label>Message *</label>
          <textarea name="message" minlength="10" maxlength="5000" required></textarea>
        </div>
        
        <!-- Turnstile CAPTCHA -->
        <div class="turnstile-container pt-2">
          <div ref="turnstile"></div>
        </div>
        
        <v-btn type="submit" value="Send" class="contact-submit-btn" x-large :loading="loading" :disabled="!turnstileToken">
          Submit
        </v-btn>
      </form>
    </div>
    <div id="form-success">
      <div class="alien-cont">
        <div id="alien">
          <img class="success-alien" src="../assets/images/alien-thumbs.png" />
        </div>
      </div>

      <div class="success-content">
        <h2>Success!</h2>
        <h3>Your form has been submitted. <br /></h3>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

const TURNSTILE_SCRIPT_ID = "cloudflare-turnstile-script";
let turnstileScriptPromise;

function loadTurnstileScript() {
  if (window.turnstile) {
    return Promise.resolve();
  }

  if (turnstileScriptPromise) {
    return turnstileScriptPromise;
  }

  turnstileScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID);
    const script = existingScript || document.createElement("script");

    script.addEventListener("load", resolve, { once: true });
    script.addEventListener("error", reject, { once: true });

    if (!existingScript) {
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });

  return turnstileScriptPromise;
}

export default {
  name: "contact-form",
  data() {
    return {
      loading: false,
      turnstileToken: null,
      turnstileWidgetId: null,
      formStartedAt: Date.now(),
      formData: {
        user_name: "",
        user_email: "",
        message: "",
      },
    };
  },
  mounted() {
    this.initializeTurnstile();
  },
  beforeDestroy() {
    if (window.turnstile && this.turnstileWidgetId !== null) {
      window.turnstile.remove(this.turnstileWidgetId);
    }
  },
  methods: {
    async initializeTurnstile() {
      try {
        await loadTurnstileScript();
        this.turnstileWidgetId = window.turnstile.render(this.$refs.turnstile, {
          sitekey: "0x4AAAAAACLJqBbfh2KloFa7",
          action: "contact-form",
          callback: (token) => {
            this.turnstileToken = token;
          },
          "expired-callback": () => {
            this.turnstileToken = null;
          },
          "error-callback": () => {
            this.turnstileToken = null;
          },
        });
      } catch (error) {
        console.error("Turnstile failed to load", error);
      }
    },
    resetTurnstile() {
      this.turnstileToken = null;
      if (window.turnstile && this.turnstileWidgetId !== null) {
        window.turnstile.reset(this.turnstileWidgetId);
      }
    },
    async sendEmail(e) {
      if (!this.turnstileToken) {
        alert('Please complete the CAPTCHA verification');
        return;
      }

      this.loading = true;
      
      // Get form data
      const formData = new FormData(e.target);
      const data = {
        user_name: formData.get('user_name'),
        user_email: formData.get('user_email'),
        message: formData.get('message'),
        website: formData.get('website'),
        form_started_at: formData.get('form_started_at'),
        turnstile_token: this.turnstileToken,
      };

      try {
        // Send to Netlify Function
        const response = await axios.post('/.netlify/functions/send-email', data);
        
        console.log("SUCCESS!", response.data);
        this.loading = false;
        
        // Hide form and show success message
        document
          .getElementById("form-content")
          .classList.add("display-none");
        document.getElementById("form-success").classList.add("show-alien");
        
        // Animate alien
        const alien = document.getElementById("alien");
        setTimeout(() => {
          alien.style.transition = "all 3s ease-in-out";
          alien.style.height = "258px";
        }, 2000);
        
        // Reset form
        this.$refs.contactForm.reset();
        this.formStartedAt = Date.now();
        this.resetTurnstile();
        
        // Reset after 10 seconds
        setTimeout(() => {
          document
            .getElementById("form-content")
            .classList.remove("display-none");
          document.getElementById("form-success").classList.remove("show-alien");
          alien.style.height = "0px";
        }, 10000);
      } catch (error) {
        console.error("FAILED...", error);
        this.loading = false;
        this.resetTurnstile();
        const errorMessage = error.response?.data?.message || "Failed to send message. Please try again or email directly.";
        alert(errorMessage);
      }
    },
  },
};
</script>

<style lang="scss">
@import "../scss/styles.scss";

#contact-form {
  position: relative;
  max-width: 1000px;
  min-height: 707px;
  margin: auto;
  color: $white;
  padding: 2rem 1rem;
  @include md {
    margin-bottom: 3rem;
  }
  .form-content {
    position: relative;
    padding: 1rem 1rem 3rem;
    border: 3px solid rgb(255 255 255 / 75%);
    box-shadow: 8px 8px 0 rgb(86 86 86 / 30%);
    border-radius: 20px;
    .spam-trap {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip: rect(0 0 0 0);
      clip-path: inset(50%);
      white-space: nowrap;
    }
    @include md {
      border: 6px solid rgb(255 255 255 / 75%);
      padding: 1rem 1.4rem 3rem;
      box-shadow: 13px 15px 0 rgb(86 86 86 / 30%);
    }
    .form-group {
      position: relative;
      label {
        display: block;
        font-family: "Montserrat";
        font-weight: 600;
        font-size: 1.2rem;
        padding-left: 1.2rem;
      }
      label {
        margin: 1rem 0;
      }
      input,
      textarea {
        color: $white;
        width: 100%;
        height: 100%;
        background: transparent;
        outline: none;
        border: 3px solid rgb(255 255 255 / 75%);
        box-shadow: 8px 8px 0 rgb(88 88 88 / 30%);
        border-radius: 20px;
        @include md {
          border: 5px solid rgb(255 255 255 / 75%);
          box-shadow: 10px 10px 0 rgb(88 88 88 / 30%);
        }
      }
      input {
        font-size: 1.2rem;
        padding: 10px 0 10px 20px;
      }
      textarea {
        font-size: 1rem;
        height: 180px;
        padding: 16px 15px;
      }
    }
    .turnstile-container {
      display: flex;
      justify-content: center;
      margin: 1rem 0;
    }
    .contact-submit-btn {
      background-color: $mcf-blue;
      color: $white;
      font-weight: 600;
      margin-top: 2rem;
      width: 100%;
      box-shadow: 8px 8px 0 rgb(88 88 88 / 30%);
      @include md {
        box-shadow: 10px 10px 0 rgb(88 88 88 / 30%);
      }
      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }
  }
  #form-success {
    display: none;
    text-align: center;
    overflow: hidden;
    padding: 1rem 0 0;
    border: 3px solid rgb(255 255 255 / 75%);
    box-shadow: 8px 8px 0 rgb(86 86 86 / 30%);
    border-radius: 20px;

    &.show-alien {
      display: block;
    }
    @include md {
      border: 6px solid rgb(255 255 255 / 75%);
      box-shadow: 13px 15px 0 rgb(86 86 86 / 30%);
    }
    .alien-cont {
      margin-bottom: 0;
    }
    #alien {
      height: 0px;
      overflow: hidden;
    }
    .success-alien {
      width: 80%;
      max-width: 270px;
    }
    .success-content {
      background: white;
      h2 {
        color: #8abe3e;
        font-weight: 900;
        font-size: 3rem;
        padding-top: 2rem;
      }
      h3 {
        color: rgba(0, 0, 0, 0.87);
        padding-bottom: 5rem;
      }
    }
  }
}
</style>
